import axios from "axios";
import { ArtworkPreviewList } from "./ArtworkPreview";
import FileDropzone from "../../components/FileDropzone";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Gavel, ShieldCheck, Sparkles, Lock, Package, CheckCircle2, AlertTriangle } from "lucide-react";
import { DeliveryAddressForm, CheckoutBreakdown, useCheckoutQuote, emptyAddress, addressValid } from "./Checkout";
import { useAuth } from "../../auth/AuthContext";
import { openRazorpayCheckout } from "../../lib/razorpay";
import { GrossBidNotice } from "./GrossBid";
import { GstClassConfirm } from "./GstClass";
import { API, money, Modal, QUANTITY_UNITS, qtyLabel, PerUnit } from "./shared";

// ============================================================
// MODALS
// ============================================================
export function JobModal({ close, refresh }) {
  const [f, setF] = useState({
    title: "", category: "Packaging", gst_class: "cartons", quantity: 1000, quantity_unit: "units", dimensions: "", stock: "",
    finishes: "None", ship_to: "", delivery_pincode: "", need_by_date: "", artwork_status: "Ready artwork", bid_window: 12,
    capability_tags: "",
    press_type: "", substrate: "", post_press: "", product_type: "",
    buyer_city: "", buyer_state: "", max_distance_km: 500,
  });
  const [ref, setRef] = useState({ press_types: [], post_press: [], product_capabilities: [] });
  const [artworkFiles, setArtworkFiles] = useState([]);
  useEffect(() => { axios.get(`${API}/capabilities/reference`).then(r => setRef(r.data)); }, []);
  const change = e => {
    const next = { ...f, [e.target.name]: e.target.value };
    // When buyer picks Procurement (raw materials / consumables), artwork isn't
    // needed — flip the artwork select to "No artwork required". Buyer can still
    // override if they want a branded SKU.
    if (e.target.name === "category") next.gst_class = GST_CLASS_OPTIONS[e.target.value][0][0];
    if (e.target.name === "category" || e.target.name === "gst_class") next.quantity_unit = UNIT_DEFAULTS[next.gst_class] || "units";
    if (e.target.name === "category" && e.target.value === "Procurement" && f.finishes === "None") next.finishes = "";
    if (e.target.name === "category" && e.target.value !== "Procurement" && !f.finishes) next.finishes = "None";
    if (e.target.name === "category" && e.target.value === "Procurement" && f.artwork_status !== "No artwork required") {
      next.artwork_status = "No artwork required";
    }
    // Flipping back from Procurement → restore a sensible default.
    if (e.target.name === "category" && e.target.value !== "Procurement" && f.artwork_status === "No artwork required") {
      next.artwork_status = "Ready artwork";
    }
    setF(next);
  };
  const submit = async e => {
    e.preventDefault();
    const required_capabilities = {
      press_type: f.press_type || null,
      substrate: f.substrate || f.stock || null,
      post_press: f.post_press.split(",").map(s => s.trim()).filter(Boolean),
      product_type: f.product_type || null,
      quantity: Number(f.quantity),
      buyer_city: f.buyer_city || null,
      buyer_state: f.buyer_state || null,
      max_distance_km: Number(f.max_distance_km) || null,
    };
    try {
      await axios.post(`${API}/jobs`, {
        title: f.title, category: f.category, gst_class: f.gst_class, quantity: Number(f.quantity), quantity_unit: f.quantity_unit, dimensions: f.dimensions || "",
        stock: f.stock, finishes: f.finishes || "None", ship_to: f.ship_to, delivery_pincode: f.delivery_pincode, need_by_date: f.need_by_date || null, artwork_status: f.artwork_status,
        bid_window: Number(f.bid_window),
        capability_tags: f.capability_tags.split(",").map(s => s.trim()).filter(Boolean),
        press_ready_file_ids: artworkFiles.map(x => x.id),
        required_capabilities,
      });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Could not post the request. Please check the form."));
      return;
    }
    await refresh();
    close();
  };
  return (
    <Modal title="Get Instant Quotes" close={close}>
      <form onSubmit={submit} className="form-grid">
        <label>Category
          <select data-testid="job-category-select" name="category" value={f.category} onChange={change}>
            <option>Packaging</option><option>Print</option><option>Procurement</option>
          </select>
        </label>
        <label>{f.category === "Procurement" ? "What are you procuring?" : "What are you printing?"}
          <select data-testid="job-gst-class-select" name="gst_class" value={f.gst_class} onChange={change}>
            {f.category === "Procurement"
              ? ["Materials", "Finishing", "Services", "Other"].map(g => (
                  <optgroup key={g} label={g}>
                    {GST_CLASS_OPTIONS.Procurement.filter(o => o[2] === g).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </optgroup>
                ))
              : GST_CLASS_OPTIONS[f.category].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        {specFields(f.category, f.gst_class).map(([name, label, required, ph]) => (
          <label key={name}>{label}
            <input data-testid={`job-${name}-input`} name={name} value={f[name]} onChange={change} required={required} placeholder={ph}/>
          </label>
        ))}
        <label>Delivery pincode
          <input data-testid="job-delivery-pincode-input" name="delivery_pincode" value={f.delivery_pincode} required inputMode="numeric" maxLength={6} pattern="\d{6}" title="6-digit pincode"
            onChange={e => setF({ ...f, delivery_pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="e.g. 423601"/>
          <small style={{display:"block",color:"#64748B",marginTop:4,fontSize:11,textTransform:"none",letterSpacing:0,fontWeight:400}}>Suppliers quote a fixed delivery charge to this pincode. It can't be changed at checkout.</small>
        </label>
        <label>Need by (optional)
          <input data-testid="job-need-by-input" type="date" name="need_by_date" value={f.need_by_date} onChange={change} min={(() => { const d = new Date(Date.now() + 864e5); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })()}/>
          <small style={{display:"block",color:"#64748B",marginTop:4,fontSize:11,textTransform:"none",letterSpacing:0,fontWeight:400}}>We flag which bids arrive in time (production + transit days).</small>
        </label>
        <label>Quantity
          <div style={{display:"flex",gap:8}}>
            <input data-testid="job-quantity-input" name="quantity" type="number" min="0" step="any" required value={f.quantity} onChange={change} style={{flex:"0 0 38%",minWidth:0}}/>
            <select data-testid="job-quantity-unit-select" name="quantity_unit" value={f.quantity_unit} onChange={change} style={{flex:1,minWidth:0}} title={(QUANTITY_UNITS.find(u => u[0] === f.quantity_unit) || [])[1]}>
              {QUANTITY_UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </label>
        <label>Artwork
          <select data-testid="job-artwork-select" name="artwork_status" value={f.artwork_status} onChange={change}>
            <option>Ready artwork</option>
            <option>I need artwork created</option>
            <option>No artwork required</option>
          </select>
          {f.artwork_status === "No artwork required" && (
            <small style={{display:"block",color:"#64748B",marginTop:4,fontSize:11}}>Supplier can begin production from the specs. You can still upload optional reference files below.</small>
          )}
        </label>
        <label>Bid window
          <select data-testid="job-window-select" name="bid_window" value={f.bid_window} onChange={change}>
            <option value="4">4 hours</option><option value="12">12 hours</option><option value="24">24 hours</option>
          </select>
        </label>
        {f.category !== "Procurement" && <label style={{ gridColumn: "span 2" }}>Legacy capabilities (optional, comma-separated)
          <input data-testid="job-capability-input" name="capability_tags" value={f.capability_tags} onChange={change} placeholder="Kraft substrate, Flexo"/>
        </label>}

        {f.category !== "Procurement" && <div className="modal-full-row required-caps-block" data-testid="required-caps-block">
          <div className="req-caps-heading">
            <b>5-Pillar Capability Requirements</b>
            <small>Suppliers whose profile hits ≥60% across these pillars will be notified.</small>
          </div>
          <div className="req-caps-grid">
            <label>Press type
              <select data-testid="job-press-type" name="press_type" value={f.press_type} onChange={change}>
                <option value="">Any</option>
                {ref.press_types.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label>Product type (hard gate)
              <select data-testid="job-product-type" name="product_type" value={f.product_type} onChange={change}>
                <option value="">Any</option>
                {(ref.product_capabilities || []).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label>Preferred substrate
              <input data-testid="job-substrate" name="substrate" value={f.substrate} onChange={change} placeholder="Kraft 120gsm"/>
            </label>
            <label>Required finishes (comma-separated)
              <input data-testid="job-post-press" name="post_press" value={f.post_press} onChange={change} placeholder="Spot UV, Die-cut"/>
            </label>
            <label>Buyer city
              <input data-testid="job-buyer-city" name="buyer_city" value={f.buyer_city} onChange={change} placeholder="Austin"/>
            </label>
            <label>Buyer state
              <input data-testid="job-buyer-state" name="buyer_state" value={f.buyer_state} onChange={change} placeholder="TX"/>
            </label>
            <label>Max sourcing distance (km)
              <input data-testid="job-max-distance" name="max_distance_km" type="number" value={f.max_distance_km} onChange={change}/>
            </label>
          </div>
        </div>}

        {(f.artwork_status === "Ready artwork" || f.artwork_status === "No artwork required") && (
          <div className="modal-full-row">
            <label className="dz-label">
              {f.artwork_status === "Ready artwork"
                ? "Press-ready assets (released to winner)"
                : "Reference files (optional · released to winner)"}
            </label>
            <FileDropzone
              testid="artwork-dropzone"
              scope="artwork"
              accept="application/pdf,image/*,.dxf,.ai,.eps,.zip"
              value={artworkFiles}
              onChange={setArtworkFiles}
              label={f.artwork_status === "Ready artwork" ? "Drop your press-ready PDF + dieline" : "Optional: specs, swatches or sample photos"}
              hint="PDF, DXF, AI, EPS or ZIP — up to 100 MB each · only the winning printer will be able to download"
            />
          </div>
        )}
        <div className="form-actions">
          <button data-testid="job-cancel-button" type="button" className="secondary-btn" onClick={close}>Cancel</button>
          <button data-testid="job-submit-button" className="primary-btn">Submit for review <ChevronDown size={15}/></button>
        </div>
      </form>
    </Modal>
  );
}

export function BriefModal({ close, refresh }) {
  const [f, setF] = useState({ brand_name: "", title: "", dimensions: "", substrate: "", style: "", logo_status: "Logo ready", notes: "" });
  const change = e => setF({ ...f, [e.target.name]: e.target.value });
  const submit = async e => {
    e.preventDefault();
    await axios.post(`${API}/briefs`, f);
    await refresh();
    close();
  };
  return (
    <Modal title="Create an artwork brief" close={close}>
      <form onSubmit={submit} className="form-grid">
        {[["brand_name", "Brand name"], ["title", "Brief title"], ["dimensions", "Pack dimensions"], ["substrate", "Preferred substrate"], ["style", "Visual direction"], ["notes", "Notes for agencies"]].map(x => (
          <label key={x[0]}>{x[1]}
            {x[0] === "notes"
              ? <textarea data-testid={`brief-${x[0]}-input`} name={x[0]} value={f[x[0]]} onChange={change}/>
              : <input data-testid={`brief-${x[0]}-input`} name={x[0]} value={f[x[0]]} onChange={change} required/>}
          </label>
        ))}
        <label>Logo status
          <select data-testid="brief-logo-select" name="logo_status" value={f.logo_status} onChange={change}>
            <option>Logo ready</option><option>Needs logo direction</option>
          </select>
        </label>
        <div className="form-actions">
          <button data-testid="brief-cancel-button" type="button" className="secondary-btn" onClick={close}>Cancel</button>
          <button data-testid="brief-submit-button" className="primary-btn">Send to agencies <Sparkles size={15}/></button>
        </div>
      </form>
    </Modal>
  );
}

export function AwardSummarySend({ jobId }) {
  const [sending, setSending] = useState(null); // "email" | "whatsapp" | null
  const [sent, setSent] = useState({ email: false, whatsapp: false });
  const [err, setErr] = useState("");
  const send = async (channel) => {
    setSending(channel); setErr("");
    try {
      await axios.post(`${API}/jobs/${jobId}/award-summary/send`, { channels: [channel] });
      setSent(s => ({ ...s, [channel]: true }));
      toast.success(channel === "email" ? "Summary sent to your inbox" : "WhatsApp summary sent", {
        description: "Supplier identity is NOT included.",
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || `Could not send ${channel} summary.`);
    } finally {
      setSending(null);
    }
  };
  return (
    <div data-testid="award-summary-send" style={{
      border:"1px solid #E2E8F0", borderRadius:8, padding:"12px 14px", margin:"6px 0 14px",
      background:"#FAFBFF",
    }}>
      <div style={{fontSize:12, fontWeight:600, color:"#1E293B", marginBottom:8}}>Keep a copy of this award</div>
      <div style={{fontSize:11, color:"#64748B", marginBottom:10}}>We'll send the price, artwork state, and next steps. No supplier identity included.</div>
      <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
        <button
          data-testid="award-summary-email"
          className="secondary-btn"
          disabled={sending==="email" || sent.email}
          onClick={() => send("email")}
          style={{fontSize:12, padding:"6px 12px"}}
        >
          {sent.email ? "✓ Email sent" : sending==="email" ? "Sending…" : "Email me a copy"}
        </button>
        <button
          data-testid="award-summary-whatsapp"
          className="secondary-btn"
          disabled={sending==="whatsapp" || sent.whatsapp}
          onClick={() => send("whatsapp")}
          style={{fontSize:12, padding:"6px 12px"}}
        >
          {sent.whatsapp ? "✓ WhatsApp sent" : sending==="whatsapp" ? "Sending…" : "WhatsApp me a copy"}
        </button>
      </div>
      {err && <div data-testid="award-summary-err" style={{marginTop:8, fontSize:11, color:"#991B1B"}}>{err}</div>}
    </div>
  );
}

export function PaymentModal({ job, bid, close, refresh, onAwarded }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [artworkAck, setArtworkAck] = useState(false);
  const [addr, setAddr] = useState(() => ({ ...emptyAddress(user), pincode: job?.delivery_pincode || "" }));
  const { quote, loading: quoting, err: quoteErr } = useCheckoutQuote(job.id, bid.id, addr);
  const total = quote?.total_inr;
  // Compute the soft-gate flag so the submit button can know whether to block.
  const artworkGateBlocked = job?.artwork_required !== false && (job?.press_ready_assets || []).length === 0;
  const pay = async () => {
    setLoading(true); setError("");
    try {
      const [cfg, order] = await Promise.all([
        axios.get(`${API}/razorpay/config`).then(r => r.data),
        axios.post(`${API}/razorpay/jobs/${job.id}/award-escrow/order`, { bid_id: bid.id, delivery_address: { ...addr, phone: addr.phone.replace(/\D/g, "").slice(-10) } }).then(r => r.data),
      ]);
      // Guard against stale TEST keys in production
      if (cfg.mode && cfg.mode !== "live" && window.location.hostname.endsWith("goprintpack.com")) {
        setError("Payments are temporarily disabled (gateway in test mode). Please contact support.");
        setLoading(false);
        return;
      }
      const resp = await openRazorpayCheckout({
        keyId: cfg.key_id,
        order,
        name: "GoPrintPack · Escrow",
        description: `Award ${job.title} · ${bid?.anon_label || bid?.supplier_name}`,
        prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
        notes: { kind: "job_escrow", job_id: job.id, bid_id: bid.id },
      });
      await axios.post(`${API}/razorpay/verify`, resp);
      // Pull the now-awarded job (status flipped by verify) so we can tailor
      // the toast — specifically to tell the buyer if artwork is still owed.
      const jobRes = await axios.get(`${API}/jobs/${job.id}`).then(r => r.data);
      const assetCount = (jobRes?.press_ready_assets || []).length;
      const artworkOwed = jobRes?.artwork_required !== false && assetCount === 0;
      // Fire the award toast with contextual follow-up text so buyers who
      // skipped artwork earlier know production is blocked until they upload.
      toast.success("Supplier awarded · escrow secured", {
        description: artworkOwed
          ? `${job.title} · ${money(total)} held by Razorpay. Open the job to upload press-ready artwork — production is blocked until you do.`
          : `${job.title} · ${money(total)} held by Razorpay. Supplier identity stays anonymous.`,
        duration: artworkOwed ? 8000 : 5000,
      });
      await refresh();
      // Close drawer + modal immediately per product spec. Buyer lands back on
      // the job list where the row now shows "awarded".
      if (onAwarded) onAwarded();
      else close();
    } catch (e) {
      if (e?.code === "USER_CANCELLED") setError("Payment cancelled — escrow not funded.");
      else setError(e?.description || e?.response?.data?.detail || "Escrow payment failed. Try again.");
    } finally {
      setLoading(false);
    }
  };
  if (result) {
    const jobRow = result.job || {};
    const assetCount = (jobRow.press_ready_assets || []).length;
    const artworkRequired = jobRow.artwork_required !== false; // undefined → treat as required (legacy)
    let artState;
    if (!artworkRequired) {
      artState = {
        tone: "neutral",
        icon: <Package size={16}/>,
        title: assetCount ? "No artwork required · references attached" : "No artwork required",
        body: assetCount
          ? `Supplier can begin production from your specs. ${assetCount} reference file${assetCount>1?"s":""} released.`
          : "This is a procurement run — the supplier starts production immediately from the specs you provided.",
      };
    } else if (assetCount) {
      artState = {
        tone: "ok",
        icon: <CheckCircle2 size={16}/>,
        title: `Artwork released · ${assetCount} file${assetCount>1?"s":""}`,
        body: "The supplier now has access to your press-ready assets and can proceed to pre-press.",
      };
    } else {
      artState = {
        tone: "warn",
        icon: <AlertTriangle size={16}/>,
        title: "Artwork pending",
        body: "Supplier is awarded but production is blocked until you upload press-ready files. Open the job to attach them now.",
      };
    }
    const toneStyle = {
      ok:      { bg:"#ECFDF5", bd:"#A7F3D0", fg:"#065F46" },
      warn:    { bg:"#FFFBEB", bd:"#FDE68A", fg:"#92400E" },
      neutral: { bg:"#F1F5F9", bd:"#CBD5E1", fg:"#334155" },
    }[artState.tone];
    return (
      <Modal title="Supplier awarded · escrow secured" close={close}>
        <div className="drawer-callout ok">
          <ShieldCheck size={17}/>
          <div>
            <b>Funds in Razorpay escrow · {money(result.winning_bid.price)}</b>
            <span>Razorpay payment {result.escrow.id}</span>
          </div>
        </div>
        <div className="detail-block">
          <label>Winning bid (identity stays anonymous)</label>
          <b data-testid="award-anon-label">{result.winning_bid.anon_label}</b>
          <p>Match score {result.winning_bid.masked_score ?? "—"} · {result.winning_bid.turnaround_days ?? "—"} day turnaround</p>
          <p style={{color:"#64748B",fontSize:12,marginTop:6,display:"flex",alignItems:"center",gap:6}}>
            <Lock size={13}/> Supplier identity remains masked throughout production. Coordinate anonymously via the in-job chat.
          </p>
        </div>
        <div
          data-testid="award-artwork-state"
          data-tone={artState.tone}
          style={{
            display:"flex", gap:10, alignItems:"flex-start",
            padding:"12px 14px", borderRadius:8,
            background: toneStyle.bg, border:`1px solid ${toneStyle.bd}`,
            color: toneStyle.fg, margin:"14px 0",
          }}
        >
          <span style={{marginTop:1}}>{artState.icon}</span>
          <div style={{fontSize:13, lineHeight:1.45}}>
            <b style={{display:"block", marginBottom:2}}>{artState.title}</b>
            <span>{artState.body}</span>
          </div>
        </div>
        <AwardSummarySend jobId={jobRow.id || job.id}/>
        <button data-testid="payment-done-button" className="primary-btn full" onClick={close}>Done</button>
      </Modal>
    );
  }
  return (
    <Modal title="Award & fund escrow" close={close}>
      <div className="payment-summary">
        <span>Awarding to</span>
        <b>{bid?.anon_label || bid?.supplier_name}</b>
        <strong>{money(bid?.price)} <small style={{fontSize:11,fontWeight:500}}>base · excl. GST & delivery</small></strong>
        <PerUnit price={bid?.price} qty={job?.quantity} unit={job?.quantity_unit} testid="payment-per-unit"/>
        {bid?.delivered_in_days ? <small data-testid="payment-delivered-in" style={{fontWeight:600,color:"#1E3A8A"}}>Delivered in {bid.delivered_in_days} days · {bid.turnaround_days}d production + {bid.transit_days}d transit{bid.transit_source === "printer_locked" ? " (committed)" : bid.transit_source === "shiprocket_live" ? " (live courier estimate)" : bid.transit_source === "printer_estimate" ? " (printer's estimate)" : ""}</small> : null}
        <small>{bid?.shipping_mode === "own_courier" ? `Printer ships via own courier · delivery fixed at ${money(bid?.delivery_charge_inr ?? bid?.estimated_delivery_inr)} + GST.` : "Ships via Shiprocket under GoPrintPack branding."} Funds held in Razorpay escrow until delivery confirmation.</small>
      </div>
      <div className="drawer-callout">
        <Lock size={17}/>
        <div>
          <b>Supplier identity stays anonymous — before and after escrow</b>
          <span>After award you'll still see this supplier only as <b>{bid?.anon_label || "the awarded press"}</b> across chat, artwork handoff, proofs, and delivery. No company name, phone, email, or GSTIN is ever shared. Match score: {bid?.masked_score} · Weighted Price 50% / Performance 30% / Quality 20%.</span>
        </div>
      </div>
      <DeliveryAddressForm value={addr} onChange={setAddr} lockedPincode={job?.delivery_pincode}/>
      <CheckoutBreakdown quote={quote} loading={quoting} err={quoteErr}/>
      <div data-testid="escrow-trust-row" style={{display:"flex",gap:10,alignItems:"flex-start",background:"#F0FDF4",border:"1px solid #BBF7D0",borderRadius:8,padding:"12px 14px",marginTop:8}}>
        <ShieldCheck size={17} style={{color:"#059669",marginTop:1,flexShrink:0}}/>
        <div style={{fontSize:12.5,color:"#065F46",lineHeight:1.55}}>
          <b style={{display:"block",marginBottom:2,color:"#064E3B"}}>Your {total ? money(total) : "payment"} is protected by Razorpay escrow</b>
          Razorpay (RBI PA/PG regulated) holds the funds and releases them to the supplier <b>only</b> when you confirm delivery, or 7 days after dispatch — not before. Raise a dispute any time to pause the clock.
        </div>
      </div>
      <ArtworkGate job={job} acknowledged={artworkAck} onAcknowledge={() => setArtworkAck(true)} close={close}/>
      {error && <div style={{padding:10,margin:"10px 0",background:"#FEF2F2",border:"1px solid #FCA5A5",color:"#991B1B",borderRadius:6,fontSize:12}} data-testid="payment-err">{error}</div>}
      <div className="form-actions">
        <button data-testid="payment-cancel-button" className="secondary-btn" onClick={close} disabled={loading}>Back</button>
        <button
          data-testid="payment-confirm-button"
          className="primary-btn"
          disabled={loading || (artworkGateBlocked && !artworkAck) || !addressValid(addr) || !quote || quoting}
          onClick={pay}
        >
          {loading ? "Opening Razorpay…" : (artworkGateBlocked && !artworkAck) ? "Acknowledge artwork gap first" : !addressValid(addr) ? "Add delivery address" : total ? `Pay ${money(total)} & award` : "Fund escrow & award"} <ShieldCheck size={15}/>
        </button>
      </div>
    </Modal>
  );
}

/** Soft gate — if the job needs artwork but the buyer uploaded nothing, surface
 *  an amber warning that must be explicitly acknowledged before escrow can be
 *  funded. Does NOT hard-block; the buyer can still proceed after ticking the
 *  acknowledgement (useful for buyers who plan to upload right after awarding).
 */
export function ArtworkGate({ job, acknowledged, onAcknowledge, close }) {
  const needsArtwork = job?.artwork_required !== false; // undefined → required (legacy)
  const assetCount = (job?.press_ready_assets || []).length;
  if (!needsArtwork || assetCount > 0) return null;
  return (
    <div
      data-testid="artwork-gate-warning"
      style={{
        padding:"12px 14px", borderRadius:8, margin:"10px 0",
        background:"#FFFBEB", border:"1px solid #FDE68A", color:"#92400E",
      }}
    >
      <div style={{display:"flex", gap:10, alignItems:"flex-start"}}>
        <AlertTriangle size={16} style={{marginTop:1, flexShrink:0}}/>
        <div style={{fontSize:13, lineHeight:1.5}}>
          <b style={{display:"block", marginBottom:3}}>No press-ready artwork uploaded</b>
          <span>The printer will stay blocked at pre-press until you attach the files. You can upload now (recommended) or acknowledge and attach later from the job workspace.</span>
          <label style={{display:"flex", alignItems:"center", gap:8, marginTop:10, cursor:"pointer", fontSize:12, fontWeight:600}}>
            <input
              type="checkbox"
              data-testid="artwork-gate-ack"
              checked={acknowledged}
              onChange={e => e.target.checked && onAcknowledge()}
            />
            I understand — I'll upload the press-ready files right after awarding.
          </label>
          <button
            data-testid="artwork-gate-upload"
            onClick={close}
            style={{
              marginTop:8, background:"transparent", border:"none",
              color:"#B45309", fontSize:11, fontWeight:700, cursor:"pointer", padding:0,
              textDecoration:"underline",
            }}
          >
            ← Go back and upload the artwork first
          </button>
        </div>
      </div>
    </div>
  );
}

export function BidModal({ job, supplierId, close, refresh, onSuccess, existing }) {
  const [f, setF] = useState(() => existing ? {
    price: existing.price ?? "", turnaround_days: existing.turnaround_days ?? 8, production_note: existing.production_note || "", shipping_mode: existing.shipping_mode || "shiprocket",
    ship_weight_kg: existing.ship_weight_kg ?? "", delivery_charge_inr: existing.delivery_charge_inr ?? "", delivery_terms_ack: existing.shipping_mode === "own_courier", transit_days: existing.transit_days ?? "",
    weight_ack: !!existing.weight_ack, gst_class_proposed: existing.gst_class_proposed || null, gst_class_reason: existing.gst_class_reason || "",
  } : { price: "", turnaround_days: 8, production_note: "", shipping_mode: "shiprocket", ship_weight_kg: "", delivery_charge_inr: "", delivery_terms_ack: false, transit_days: "", weight_ack: false, gst_class_proposed: null, gst_class_reason: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const change = e => setF({ ...f, [e.target.name]: e.target.value, ...(e.target.name === "ship_weight_kg" ? { weight_ack: false } : {}) });
  const own = f.shipping_mode === "own_courier";
  const [wEst, setWEst] = useState(null);
  useEffect(() => { axios.get(`${API}/jobs/${job.id}/weight-estimate`).then(r => setWEst(r.data.estimate)).catch(() => setWEst(null)); }, [job.id]);
  const underWeight = !!(wEst && f.ship_weight_kg && Number(f.ship_weight_kg) < 0.8 * wEst.kg);
  const submit = async e => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/bids${existing ? "?revise=true" : ""}`, {
        supplier_id: supplierId,
        price: Number(f.price),
        turnaround_days: Number(f.turnaround_days),
        production_note: f.production_note,
        shipping_mode: f.shipping_mode,
        ship_weight_kg: f.ship_weight_kg ? Number(f.ship_weight_kg) : null,
        delivery_charge_inr: own ? Number(f.delivery_charge_inr) : null,
        delivery_terms_ack: own && f.delivery_terms_ack,
        transit_days: f.transit_days ? Number(f.transit_days) : null,
        weight_ack: underWeight && f.weight_ack,
        gst_class_proposed: f.gst_class_proposed,
        gst_class_reason: f.gst_class_reason,
      });
      await refresh();
      toast.success(existing ? "Sealed bid updated" : "Sealed bid submitted", {
        description: `${job?.title} · ₹${Number(f.price).toLocaleString("en-IN")} · ${f.turnaround_days} days turnaround. We'll notify you if the buyer picks you.`,
      });
      close();
      if (onSuccess) onSuccess();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? d.map(x => String(x.msg || "").replace(/^Value error, /, "")).join(" · ") : (d || "Failed to submit bid"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={existing ? "Edit your sealed bid" : "Submit a sealed bid"} close={close}>
      <div className="payment-summary">
        <span>Sealed auction</span>
        <b>{job?.title}</b>
        <small>{qtyLabel(job)} · {job?.dimensions} {job?.price_floor > 0 && `· Floor ${money(job.price_floor)}`}</small>
      </div>
      <div className="drawer-callout">
        <Lock size={17}/>
        <div>
          <b>One sealed offer per supplier</b>
          <span>Your bid is private. Buyers see only your masked score, radius, rating & turnaround.</span>
        </div>
      </div>
      {(job.press_ready_assets || []).length > 0 && <ArtworkPreviewList jobId={job.id}/>}
      <form onSubmit={submit} className="form-grid bid-form">
        <label>Gross bid price (INR) — incl. our commission, excl. GST & delivery
          <input data-testid="bid-price-input" name="price" type="number" min="1" step="0.01" value={f.price} onChange={change} required/>
          <PerUnit price={f.price} qty={job?.quantity} unit={job?.quantity_unit} testid="bid-price-per-unit" style={{color:"#1E3A8A",fontWeight:600,textTransform:"none",letterSpacing:0}}/>
          <small style={{fontWeight:400,color:"#64748b",textTransform:"none",letterSpacing:0}}>GST and delivery are added at buyer checkout. The buyer sees only this price.</small>
        </label>
        <GrossBidNotice amount={f.price} gstClass={f.gst_class_proposed || job.gst_class || job.gst_class_effective}/>
        {(job.gst_class || job.gst_class_effective) && <GstClassConfirm jobClass={job.gst_class || job.gst_class_effective} value={f.gst_class_proposed} reason={f.gst_class_reason}
          onChange={(v, r) => setF(x => ({ ...x, gst_class_proposed: v, gst_class_reason: r }))}/>}
        <label>Shipping
          <select data-testid="bid-shipping-mode" name="shipping_mode" value={f.shipping_mode} onChange={change}>
            <option value="shiprocket">GoPrintPack ships via Shiprocket (blind label)</option>
            <option value="own_courier">I ship with my own courier (I quote delivery)</option>
          </select>
        </label>
        <label>{own ? "Shipment weight (kg) — locked once you bid" : "Est. shipment weight (kg)"}
          <input data-testid="bid-weight-input" name="ship_weight_kg" type="number" min="0.1" step="0.1" value={f.ship_weight_kg} onChange={change} required readOnly={!!existing} title={existing ? "Weight is locked once you bid" : undefined} placeholder="e.g. 12"/>
        </label>
        <WeightHint est={wEst} under={underWeight} f={f} setF={setF}/>
        {own && <OwnCourierQuote job={job} f={f} setF={setF}/>}
        <label>Production turnaround (days)
          <input data-testid="bid-turnaround-input" name="turnaround_days" type="number" min="1" max="90" value={f.turnaround_days} onChange={change} required/>
        </label>
        <label>{own ? "Courier transit days — locked once you bid" : "Your transit estimate (days)"}
          <input data-testid="bid-transit-days-input" name="transit_days" type="number" min="1" max="30" step="1" value={f.transit_days} onChange={change} required placeholder="e.g. 3"/>
          {job?.need_by_date && <NeedByHint needBy={job.need_by_date} days={Number(f.turnaround_days || 0) + Number(f.transit_days || 0)}/>}
          <small data-testid="bid-delivered-in" style={{fontWeight:600,color:"#1E3A8A",fontSize:12,fontFamily:"Outfit, sans-serif",textTransform:"none",letterSpacing:0}}>
            {own ? "Buyer sees" : "Used only if Shiprocket's live estimate is unavailable. Buyer sees"} "Delivered in {Number(f.turnaround_days || 0) + Number(f.transit_days || 0)} days" ({f.turnaround_days || 0}d production + {f.transit_days || 0}d transit).
          </small>
        </label>
        <label>Production note (for reviewer)
          <textarea
            data-testid="bid-production-note-input"
            name="production_note"
            value={f.production_note}
            onChange={change}
            placeholder="Substrate availability, press slot, sustainability notes…"
          />
        </label>
        {error && <div className="form-error" data-testid="bid-error">{error}</div>}
        <div className="form-actions">
          <button data-testid="bid-cancel-button" type="button" className="secondary-btn" onClick={close} disabled={busy}>Cancel</button>
          <button data-testid="bid-submit-button" className="primary-btn" disabled={busy || (own && !f.delivery_terms_ack) || (underWeight && !f.weight_ack)}>
            {busy ? "Submitting…" : existing ? "Update sealed bid" : "Submit sealed bid"} <Gavel size={15}/>
          </button>
        </div>
      </form>
    </Modal>
  );
}

const fmtDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

function NeedByHint({ needBy, days }) {
  const arrive = new Date(Date.now() + days * 864e5);
  const ok = arrive.toISOString().slice(0, 10) <= needBy;
  return (
    <small data-testid="bid-need-by-hint" style={{display:"block",fontSize:12,fontWeight:600,textTransform:"none",letterSpacing:0,fontFamily:"Outfit, sans-serif",color: ok ? "#047857" : "#B91C1C"}}>
      Buyer needs it by {fmtDate(needBy)} · you'd deliver by {fmtDate(arrive)} {ok ? "✓ in time" : "✗ late: bids that miss the date can't win Best Value or Fastest Delivery"}
    </small>
  );
}

function WeightHint({ est, under, f, setF }) {
  if (!est) return null;
  return (
    <div data-testid="bid-weight-hint" style={{gridColumn:"1 / -1",fontSize:12,color: under ? "#92400E" : "#475569",background: under ? "#FFFBEB" : "#F8FAFC",border:`1px solid ${under ? "#FDE68A" : "#E2E8F0"}`,borderRadius:8,padding:"8px 12px",fontFamily:"Outfit, sans-serif"}}>
      Suggested weight from specs: <b data-testid="bid-weight-suggested">~{est.kg} kg</b> <span style={{color:"#94A3B8"}}>({est.basis})</span>
      {!f.ship_weight_kg && <button type="button" data-testid="bid-weight-use-suggested" onClick={() => setF(x => ({ ...x, ship_weight_kg: String(est.kg) }))} style={{marginLeft:8,border:0,background:"none",color:"#1D4ED8",fontWeight:700,cursor:"pointer",padding:0}}>Use this</button>}
      {under && (
        <label style={{display:"flex",gap:8,alignItems:"flex-start",marginTop:6,fontWeight:600,fontSize:12,fontFamily:"Outfit, sans-serif",color:"#92400E",textTransform:"none",letterSpacing:0,cursor:"pointer"}}>
          <input data-testid="bid-weight-ack" type="checkbox" checked={f.weight_ack} onChange={e => setF(x => ({ ...x, weight_ack: e.target.checked }))} style={{width:"auto",marginTop:2}}/>
          Your weight is more than 20% below the estimate. I've checked the weight; extra courier cost from under-declaring is mine.
        </label>
      )}
    </div>
  );
}

function OwnCourierQuote({ job, f, setF }) {
  const [est, setEst] = useState(null);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    const w = Number(f.ship_weight_kg);
    if (!w) { setEst(null); return; }
    const t = setTimeout(() => axios.get(`${API}/jobs/${job.id}/delivery-estimate`, { params: { weight_kg: w } })
      .then(r => { setEst(r.data); if (!touched && r.data.suggested_inr != null) setF(x => ({ ...x, delivery_charge_inr: String(r.data.suggested_inr) })); })
      .catch(() => setEst(null)), 350);
    return () => clearTimeout(t);
  }, [f.ship_weight_kg]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="own-courier-quote" style={{ gridColumn: "1 / -1", border: "1px solid #BFDBFE", background: "#EFF6FF", borderRadius: 10, padding: "12px 14px", display: "grid", gap: 8 }}>
      <label style={{ margin: 0 }}>Delivery charge to {job?.delivery_pincode || "buyer"} (₹, excl. GST)
        <input data-testid="bid-delivery-charge-input" type="number" min="0" step="0.01" required value={f.delivery_charge_inr}
          onChange={e => { setTouched(true); setF(x => ({ ...x, delivery_charge_inr: e.target.value })); }} placeholder="e.g. 450"/>
      </label>
      <small data-testid="bid-delivery-zone" style={{ fontSize: 11.5, color: "#1E3A8A", textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>
        {est?.zone_label ? `${est.zone_label} (${est.from_pincode} → ${est.to_pincode}) · rate card suggests ${money(est.suggested_inr)}. You can edit it.`
          : est && !est.has_card ? "Save a Delivery Rate Card under Capabilities to auto-fill this from weight and zone."
          : est && !est.to_pincode ? "This older request has no delivery pincode. Quote delivery to the ship-to address shown above."
          : "Enter the weight to get a suggestion from your rate card."}
      </small>
      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", margin: 0, fontSize: 12.5, fontWeight: 500, color: "#92400E", textTransform: "none", letterSpacing: 0, fontFamily: "Outfit, sans-serif", lineHeight: 1.45, cursor: "pointer" }}>
        <input data-testid="bid-delivery-ack" type="checkbox" checked={f.delivery_terms_ack} onChange={e => setF(x => ({ ...x, delivery_terms_ack: e.target.checked }))} style={{ width: "auto", marginTop: 2 }}/>
        Weight and delivery charge are final. The buyer pays upfront; if the real parcel is heavier, I pay the extra courier cost.
      </label>
    </div>
  );
}

const UNIT_DEFAULTS = { labels: "rolls", flexible_pack: "kgs", procurement: "kgs", proc_paper: "kgs", proc_corrugated: "kgs", proc_ink: "kgs",
  proc_films: "kgs", proc_tapes: "rolls", proc_adhesives: "kgs", proc_packing_aids: "kgs" };

const SPEC_FIELDS = {
  print: [["title", "Request name", true], ["dimensions", "Finished dimensions", true, "e.g. 10 x 8 x 3 in"], ["stock", "Stock / substrate", true, "e.g. Kraft 300 GSM"], ["finishes", "Finishes", true, "e.g. Matte lamination, Spot UV"], ["ship_to", "Ship-to address", true]],
  material: [["title", "Request name", true, "e.g. Kraft paper reels for May"], ["stock", "Material specification / grade", true, "e.g. Kraft 120 GSM, BF 18, natural"], ["dimensions", "Size / format (optional)", false, "e.g. 100 cm reel width, 23 x 36 in sheets"], ["finishes", "Brand / packing preference (optional)", false, "e.g. ITC or JK, reels on pallets"], ["ship_to", "Ship-to address", true]],
  service: [["title", "Request name", true, "e.g. Diwali campaign shoot"], ["stock", "Scope / deliverables", true, "e.g. 20 product photos + 2 reels"], ["dimensions", "Formats / sizes (optional)", false, "e.g. 1080 x 1350 px, A4"], ["finishes", "Preferences (optional)", false, "e.g. Brand guidelines attached"], ["ship_to", "Delivery / service address", true]],
};
SPEC_FIELDS.finishing = [["title", "Request name", true, "e.g. Matte lamination for 5,000 sheets"], ["stock", "Finishing required", true, "e.g. Thermal matte lamination + spot UV on 300 GSM board"], ["dimensions", "Sheet size (optional)", false, "e.g. 23 x 36 in"], ["finishes", "Pick-up / delivery notes (optional)", false, "e.g. Sheets ready at our press in Bhiwandi"], ["ship_to", "Delivery address", true]];
const specFields = (category, gstClass) => SPEC_FIELDS[category !== "Procurement" ? "print" : gstClass === "svc_finishing" ? "finishing" : String(gstClass).startsWith("svc_") ? "service" : "material"];

const GST_CLASS_OPTIONS = {
  Packaging: [["cartons", "Cartons & boxes (incl. rigid boxes) · GST 5%"], ["flexible_pack", "Pouches & flexible laminates · GST 18%"], ["paper_bags", "Paper bags & sacks · GST 18%"], ["labels", "Labels & stickers · GST 18%"], ["unsure", "Not sure / Other: we'll work it out"]],
  Print: [["commercial_print", "Brochures, flyers, posters, catalogues, stationery · GST 18%"], ["periodicals", "Magazines, books & periodicals · GST 5%"], ["labels", "Labels & stickers · GST 18%"], ["unsure", "Not sure / Other: we'll work it out"]],
  Procurement: [
    ["proc_paper", "Paper & paperboard (kraft, duplex, art) · GST 18%", "Materials"], ["proc_corrugated", "Corrugated sheets & board · GST 18%", "Materials"],
    ["proc_cartons", "Plain cartons & boxes · GST 5%", "Materials"], ["proc_ink", "Printing inks · GST 18%", "Materials"],
    ["proc_films", "Plastic films (BOPP / PET / LDPE) · GST 18%", "Materials"], ["proc_tapes", "Tapes & self-adhesive stock · GST 18%", "Materials"],
    ["proc_adhesives", "Adhesives & glues · GST 18%", "Materials"], ["proc_plates", "Plates, blankets & press consumables · GST 18%", "Materials"],
    ["proc_packing_aids", "Strapping, stretch wrap & void-fill · GST 18%", "Materials"], ["procurement", "Other materials & consumables · GST 18%", "Materials"],
    ["svc_advertising", "Advertising & media services · GST 18%", "Services"], ["svc_marketing", "Marketing, promotion & market research · GST 18%", "Services"],
    ["svc_branding", "Graphic design & branding · GST 18%", "Services"], ["svc_photo_video", "Photography & video production · GST 18%", "Services"],
    ["svc_business", "Business management & consulting · GST 18%", "Services"], ["svc_it", "Website, software & IT services · GST 18%", "Services"],
    ["svc_events", "Events, exhibitions & trade shows · GST 18%", "Services"], ["svc_logistics", "Courier, warehousing & logistics · GST 18%", "Services"],
    ["svc_finishing", "Finishing / job-work (lamination, foiling, UV, die-cutting, binding) · GST 18%", "Finishing"],
    ["unsure", "Not sure / Other: we'll work it out", "Other"],
  ],
};
